import {
  applyPolyfill,
  shouldApplyPolyfill
} from '@virtualstate/navigation/esnext/apply-polyfill.js';

const isPolyfill = shouldApplyPolyfill();

if (isPolyfill) {
  applyPolyfill();
}

export const init = (Alpine) => {
  Alpine.data('modal', component);
};

export const component = function () {
  return {
    open: false,
    isAjax: false,
    loading: false,
    content: null,
    interceptPaths: new Set(),
    addAjaxModalPath(qualifier) {
      try {
        if (typeof qualifier === 'string') {
          qualifier = new RegExp(qualifier)
        }

        if (qualifier instanceof RegExp) {
          let pattern = qualifier
          qualifier = (url) => new URL(url).pathname.match(pattern)
        }

        if (!qualifier || typeof qualifier !== 'function') {
          throw new Error('Modal must be a function or regex pattern/string')
        }

        navigation.addEventListener('navigate', handleNavigate);
        // document.addEventListener('mouseenter', handlePrefetch, { capture: true });
      } catch (error) {
        console.error(error)
      }

      async function handleNavigate(event) {
        if (
          (!event.userInitiated && !event.info?.modal) ||
          event.navigationType !== 'push' ||
          (event.destination.sameDocument && !event.info?.modal)
        ) {
          return;
        }

        const { destination } = event;
        const destinationState = destination.getState() || {};

        if (typeof destinationState?.modal === 'undefined') {
          const destinationUrl = new URL(destination.url);

          if (event.info?.modal || qualifier(destinationUrl, event)) {
            let prevEntry = navigation.currentEntry,
              prevState = prevEntry.getState();

            let backdrop = (prevState?.modal && prevState?.backdrop) || (prevEntry.url !== destination.url && prevEntry.key)

            event.intercept({
              async handler() {
                await navigation.navigate(destinationUrl, {
                  history: isPolyfill ? 'push' : 'replace',
                  state: {
                    ...destinationState,
                    backdrop,
                    backdropUrl: prevEntry.url,
                    modal: true
                  },
                  info: {
                    from: prevEntry
                  }
                }).finished;
              }
            });
          }
        }
      }

      function handlePrefetch(event) {
        const { target } = event

        if (target.tagName === 'A' && !target.prefetch) {
          const targetUrl = new URL(target.href);

          if (qualifier(targetUrl, event)) {
            targetUrl.searchParams.set('modal', true);

            if (
              !document.querySelector(
                `link[rel="prefetch"][href="${targetUrl}"]`
              )
            ) {
              new Promise((resolve, reject) => {
                const ms = 500;

                console.group(`Should prefetch? ${targetUrl}`);
                console.log(`Waiting ${ms}ms`);

                target.addEventListener(
                  'mouseleave',
                  (event) =>
                    reject({
                      message: 'Mouse exited target',
                      detail: event
                    }),
                  { once: true }
                );

                setTimeout(resolve, ms);
              })
                .then(() => console.log('Starting prefetch'))
                .then(() => {
                  const prefetch = document.createElement('link'),
                    loaded = new Promise(
                      (resolve) => (prefetch.onload = resolve)
                    );

                  Array.from(
                    document.querySelectorAll(
                      `a[href="${target.getAttribute('href')}"]`
                    )
                  ).forEach((link) => (link.prefetch = prefetch));

                  prefetch.rel = 'prefetch';
                  prefetch.as = 'document';
                  prefetch.href = targetUrl;

                  document.head.append(prefetch);

                  return loaded;
                })
                .then(() => console.log('Finished prefetch'))
                .catch((e) => console.log(e.message, e))
                .finally(() => console.groupEnd(targetUrl));
            }
          }
        }
      }
    },
    init() {
      if (!window.navigation) {
        console.warn('Modal requires Navigation API to be available.');

        return;
      }

      if (!navigation.activation) {
        navigation.activation = { entry: navigation.currentEntry }
      }

      const _self = this.$data;
      const initialTitle = document.title;

      document.addEventListener('modal:register', ({ detail: qualifier }) => {
        this.addAjaxModalPath(qualifier)
      });

      this.$el.addEventListener('close', async () => {
        document.title = initialTitle;

        if (!navigation.transition && navigation.currentEntry.getState()?.backdrop) {
          await navigation.traverseTo(navigation.currentEntry.getState().backdrop).finished;
        }
        
        _self.$dispatch('modal.close');
      });

      function openModal(content, { isAjax = false }) {
        _self.content = undefined;
        _self.isAjax = isAjax;

        if (!_self.$el.open) {
          _self.$el.showModal()
          _self.$dispatch('modal.open');
        }

        return Promise.resolve(content)
          .then((content) => {
            if (content) {
              _self.content = content;
            }
          })
          .catch((e) => {
            console.error(e);
            _self.content = 'An error occurred. Please try again.';
          });
      }

      function openAjaxModal({ destination, signal }) {
        return openModal(
          new Promise((resolve) => {
            let state = destination.getState() || {};

            if (state?.content) return resolve(state.content);

            let destinationUrl = new URL(destination.url);

            destinationUrl.searchParams.set('modal', true);

            resolve(
              fetch(destinationUrl, {
                signal,
                cache: 'default',
                mode: 'same-origin',
                priority: 'high',
                headers: {
                  'Content-Type': 'text/html'
                }
              })
                .then((response) => {
                  if (!response.ok) {
                    throw new Error(response.statusText)
                  }

                  return response.text()
                })
                .then((response) =>
                  new DOMParser().parseFromString(response, 'text/html')
                )
                .then((modalDocument) => {
                  modalDocument
                    .querySelector('[data-netlify-deploy-id]')
                    ?.remove();

                  let content = `<template data-document-head>
                    ${modalDocument.head.innerHTML}
                  </template>
                  ${modalDocument.body.innerHTML}`;

                  navigation.updateCurrentEntry({
                    state: {
                      ...navigation.currentEntry.getState(),
                      modal: true,
                      content,
                      title: modalDocument.title
                    }
                  })

                  return content;
                })
            );
          }),
          { isAjax: true, signal }
        );
      }

      navigation.addEventListener('currententrychange', async () => {
        const state = navigation.currentEntry.getState();

        if (state?.modal) {
          let { content, title = initialTitle } = state;

          document.title = title;
          _self.content = content;
        }
      });

      navigation.addEventListener('navigate', async (event) => {
        const { destination } = event

        if (destination.getState()?.modal) {
          event.intercept({
            async handler() {
              await openAjaxModal(event);
            },
            scroll: 'manual'
          });
        } else if (
          destination.getState()?.modal === false ||
          destination.key === navigation.currentEntry.getState()?.backdrop
        ) {
          event.intercept({
            async handler() {
              if (event.navigationType != 'push' && destination.index < navigation.activation.entry.index) {
                await navigation.reload({
                  state: {
                    ...destination.getState(),
                    modal: undefined
                  }
                }).finished;
              } else {
                _self.$el.close();
              }
            }
          });
        }
      });

      const onTransitionFinish = function handleTransitionFinish(event) {
        _self.$nextTick(() => {
          if (
            event.target === this.$el &&
            event.pseudoElement === '' &&
            !this.$el.open &&
            !this.$el.checkVisibility()
          ) {
            this.content = undefined;
          }
        });
      }.bind(this);

      this.$el.addEventListener('transitionend', onTransitionFinish);
      this.$el.addEventListener('transitioncancel', onTransitionFinish);

      Alpine.effect(() => {
        _self.loading = !_self.content;
      });

      Alpine.effect(() => {
        if (!_self.loading) {
          _self.$el.scroll({ top: 0, behavior: 'instant' })
        }
      });

      this.$dispatch('modal:register', function (url, event) {
        if (event.sourceElement?.closest('.home-hero')) return;

        return new URL(url).pathname.match(`^\/(event|cinema\/screening)\/.+`);
      });
    },
    wrapper: {
      'x-ref': 'modal',
      ':class': `{
        modal: true,
        loading: loading
      }`,
      '@click.self': "$dispatch('modal-close')",
      '@modal-close.document': '$el.close()'
    },
    closeButton: {
      type: 'button',
      '@click': "$dispatch('modal-close')",
      ':class': '"close-button"',
      'x-html': '`<span class="sr-only">Close</span>`'
    },
    loader: {
      'x-transition.opacity': '',
      'x-show': 'loading',
      'x-html': '`Loading...`',
      ':class': '"loader"'
    },
    contentWrapper: {
      'x-html': 'content || ``'
    }
  };
};

export default init;
